import { Action, combineReducers } from "redux";
import * as store from "../../../framework/customStore";
import { IEventChartOptions } from "source/models/event/eventChartOptions";
import { FrontPage, IFrontPage } from "source/models/front/frontPage";
import { IFrontPageImages } from "source/models/front/frontPageImages";

// State
export interface IFrontPageState {
    dataId: number;
    frontPage: IFrontPage;
}

export interface IFrontEventState {
    chartOptions: IEventChartOptions;
}

export interface IFrontState {
    page: IFrontPageState;
    event: IFrontEventState;
}

// Action type constants
const FRONT_SUCCESS = "FRONT_SUCCESS";
const FRONT_FAILURE = "FRONT_FAILURE";
const FRONT_IMAGES = "FRONT_IMAGES";
const FRONT_EVENTS = "FRONT_EVENTS";
const FRONT_MESSAGES = "FRONT_MESSAGES";
const FRONT_BANNER_INTERVAL = "FRONT_BANNER_INTERVAL";
const FRONT_RECORDS_USER = "FRONT_RECORDS_USER";
const FRONT_RECORDS_COMPANY = "FRONT_RECORDS_COMPANY";
const FRONT_REMOVE_PANEL = "FRONT_REMOVE_PANEL";

type T_FRONT_SUCCESS = typeof FRONT_SUCCESS;
type T_FRONT_FAILURE = typeof FRONT_FAILURE;
type T_FRONT_IMAGES = typeof FRONT_IMAGES;
type T_FRONT_EVENTS = typeof FRONT_EVENTS;
type T_FRONT_MESSAGES = typeof FRONT_MESSAGES;
type T_FRONT_BANNER_INTERVAL = typeof FRONT_BANNER_INTERVAL;
type T_FRONT_RECORDS_USER = typeof FRONT_RECORDS_USER;
type T_FRONT_RECORDS_COMPANY = typeof FRONT_RECORDS_COMPANY;
type T_FRONT_REMOVE_PANEL = typeof FRONT_REMOVE_PANEL;

// Action types
interface IFrontSuccess extends Action {
    type: T_FRONT_SUCCESS;
    frontPage: IFrontPage;
}

interface IFrontFailure extends Action {
    type: T_FRONT_FAILURE;
}

interface IFrontImages extends Action {
    type: T_FRONT_IMAGES;
    images: IFrontPageImages;
}

interface IFrontBannerInterval extends Action {
    type: T_FRONT_BANNER_INTERVAL;
    interval: number;
}

interface IFrontRecordsUser extends Action {
    type: T_FRONT_RECORDS_USER;
    recordCount: number;
    dateRange: number;
}

interface IFrontRecordsCompany extends Action {
    type: T_FRONT_RECORDS_COMPANY;
    recordCount: number;
    dateRange: number;
}

interface IFrontEvents extends Action {
    type: T_FRONT_EVENTS;
    event: IFrontEventState;
}

interface IFrontMessages extends Action {
    type: T_FRONT_MESSAGES;
    dateRange: number;
}

interface IFrontRemovePanel extends Action {
    type: T_FRONT_REMOVE_PANEL;
    panelId: string;
}

export type FrontAction =
    | IFrontSuccess
    | IFrontFailure
    | IFrontImages
    | IFrontBannerInterval
    | IFrontRecordsUser
    | IFrontRecordsCompany
    | IFrontEvents
    | IFrontMessages
    | IFrontRemovePanel;

//Actions
export const frontSuccess = (frontPage: IFrontPage): IFrontSuccess => ({
    type: FRONT_SUCCESS,
    frontPage
});

export const frontFailure = (): IFrontFailure => ({
    type: FRONT_FAILURE
});

export const frontSetImages = (images: IFrontPageImages): IFrontImages => ({
    type: FRONT_IMAGES,
    images
});

export const frontSetBannerImageChangeInterval = (interval: number): IFrontBannerInterval => ({
    type: FRONT_BANNER_INTERVAL,
    interval
});

export const frontSetRecordsUser = (recordCount: number, dateRange: number): IFrontRecordsUser => ({
    type: FRONT_RECORDS_USER,
    recordCount,
    dateRange
});

export const frontSetRecordsCompany = (recordCount: number, dateRange: number): IFrontRecordsCompany => ({
    type: FRONT_RECORDS_COMPANY,
    recordCount,
    dateRange
});

export const frontSetEvents = (event: IFrontEventState): IFrontEvents => ({
    type: FRONT_EVENTS,
    event
});

export const frontSetMessages = (dateRange: number): IFrontMessages => ({
    type: FRONT_MESSAGES,
    dateRange
});

export const frontRemovePanel = (panelId: string): IFrontRemovePanel => ({
    type: FRONT_REMOVE_PANEL,
    panelId
});

//Reducers
export const frontInitialState: IFrontState = {
    page: {
        dataId: 0,
        frontPage: null
    },
    event: {
        chartOptions: null
    }
};

const page = (state: IFrontPageState = null, action: FrontAction) => {
    switch (action.type) {
    case FRONT_SUCCESS: {
        const frontSuccess = action as IFrontSuccess;
        return {
            ...state,
            dataId: state.dataId + 1,
            frontPage: frontSuccess.frontPage
        };
    }
    case FRONT_FAILURE:
        return { ...state };
    case FRONT_IMAGES: {
        const frontImages = action as IFrontImages;
        const frontPage = new FrontPage(state.frontPage);
        frontPage.images = frontImages.images;
        return {
            ...state,
            frontPage
        };
    }
    case FRONT_BANNER_INTERVAL: {
        const frontInterval = action as IFrontBannerInterval;
        const frontPage = new FrontPage(state.frontPage);
        frontPage.banner.imageChangeInterval = frontInterval.interval;
        return {
            ...state,
            frontPage
        };
    }
    case FRONT_RECORDS_USER: {
        const recordsUser = action as IFrontRecordsUser;
        const frontPage = new FrontPage(state.frontPage);
        frontPage.records.userRecordCount = recordsUser.recordCount;
        frontPage.records.userDateRange = recordsUser.dateRange;
        return {
            ...state,
            frontPage
        };
    }
    case FRONT_RECORDS_COMPANY: {
        const recordsCompany = action as IFrontRecordsCompany;
        const frontPage = new FrontPage(state.frontPage);
        frontPage.records.companyRecordCount = recordsCompany.recordCount;
        frontPage.records.companyDateRange = recordsCompany.dateRange;
        return {
            ...state,
            frontPage
        };
    }
    case FRONT_MESSAGES: {
        const messages = action as IFrontMessages;
        const frontPage = new FrontPage(state.frontPage);
        frontPage.messages.dateRange = messages.dateRange;
        return {
            ...state,
            frontPage
        };
    }
    case FRONT_REMOVE_PANEL: {
        const removePanel = action as IFrontRemovePanel;
        const frontPage = new FrontPage(state.frontPage);
        frontPage.panels = frontPage.panels.filter(p => p.id !== removePanel.panelId);
        return {
            ...state,
            frontPage
        };
    }
    }
    return state;
};

const event = (state: IFrontEventState = null, action: FrontAction) => {
    switch (action.type) {
    case FRONT_EVENTS: {
        const frontEvents = action as IFrontEvents;
        return {
            ...state,
            chartOptions: frontEvents.event.chartOptions
        };
    }
    }
    return state;
};

//Root reducer
export const frontReducer = combineReducers({
    page,
    event
});

//Utils
export const removeFrontPanelFromState = (panelId: string) => {
    store.customStore.dispatch(frontRemovePanel(panelId));
};
